import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OfferingNotice from '../../components/OfferingNotice';
import PageHeader from '../../components/layout/PageHeader';
import contractsService from '../../services/contracts.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { CALL_CONTEXT, channelsToExecutive } from '../../lib/executiveRefusal';
import ExecutiveRefusal from '../../components/client/ExecutiveRefusal';
import { colors } from '../../styles/theme';
import { badgeFor } from '../../types/contracts';
import type { ApplicableTemplate, ContractCaseState } from '../../types/contracts';
import OnboardingRequiredPanel from '../../components/client/OnboardingRequiredPanel';
import { useClientApprovalGate } from '../../hooks/useCurrentUserProfile';
import { membershipNote } from './contracts.helpers';

/** Primary button label for each state — clarifies what action the user can
 *  take. "Start" only appears when no case exists yet. */
function actionLabelFor(state: ContractCaseState | null): string {
  if (state === null) return 'Start';
  if (state === 'pending_upload') return 'Continue';
  if (state === 'returned_to_client') return 'Review feedback';
  if (state === 'approved' || state === 'rejected') return 'View';
  return 'View status';
}

/** The order every member follows, shown while the account is still being approved: QMI
 *  signs an agreement only with a person it has identified, and handles one agreement at a
 *  time. */
function WhatComesNext(): JSX.Element {
  return (
    <section
      aria-label="What comes next"
      className="rounded-2xl p-5 mt-6 text-sm"
      style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.inkSoft }}
    >
      <h2 className="text-base font-semibold mb-2" style={{ color: colors.ink }}>
        What comes next
      </h2>
      <ol className="list-decimal pl-5 space-y-1">
        <li>We approve your account.</li>
        <li>You sign the Membership Agreement.</li>
        <li>You choose a series and sign its contract.</li>
      </ol>
      <p className="mt-2" style={{ color: colors.muted }}>
        We review one agreement at a time, so each one starts when the one before it is complete.
      </p>
    </section>
  );
}


export default function ClientContracts(): JSX.Element {
  const navigate = useNavigate();
  const gate = useClientApprovalGate();
  const [items, setItems] = useState<ApplicableTemplate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [startingFor, setStartingFor] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // A refusal that sends the client to their Account Executive, shown with the call (S-43).
  const [actionErrorCode, setActionErrorCode] = useState<string | null>(null);

  useEffect(() => {
    // Defer the listing call until onboarding is approved — pre-approval
    // there are no applicable templates to show, and the gate panel
    // below replaces the empty-state UI.
    if (gate !== 'approved') return;
    let cancelled = false;
    contractsService
      .listApplicable()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load contracts.'));
      });
    return () => {
      cancelled = true;
    };
  }, [gate]);

  if (gate === 'loading') {
    return (
      <>
        <PageHeader
          title="Contracts"
          subtitle="Documents you need to review, sign, and submit as part of your onboarding."
        />
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      </>
    );
  }

  if (gate === 'pending') {
    return (
      <>
        <PageHeader
          title="Contracts"
          subtitle="Documents you need to review, sign, and submit as part of your onboarding."
        />
        <OnboardingRequiredPanel feature="Contracts" />
        <WhatComesNext />
      </>
    );
  }

  async function handlePrimaryAction(item: ApplicableTemplate): Promise<void> {
    setActionError(null);
    setActionErrorCode(null);
    if (item.case_id) {
      navigate(`/client/contracts/${item.case_id}`);
      return;
    }
    setStartingFor(item.template_id);
    try {
      const { case_id } = await contractsService.startCase(item.current_version_id);
      navigate(`/client/contracts/${case_id}`);
    } catch (err: unknown) {
      setActionErrorCode(getApiErrorCode(err));
      setActionError(getApiErrorMessage(err, 'Could not start the contract review.'));
    } finally {
      setStartingFor(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Contracts"
        subtitle="Documents you need to review, sign, and submit as part of your onboarding."
      />

      {loadError && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {loadError}
        </div>
      )}

      {actionError && channelsToExecutive(actionErrorCode) && (
        <ExecutiveRefusal message={actionError} context={CALL_CONTEXT.contracts} />
      )}
      {actionError && !channelsToExecutive(actionErrorCode) && (
        <div
          className="rounded-lg p-4 mb-6 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {actionError}
        </div>
      )}

      {items === null && !loadError && (
        <div
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <p style={{ color: colors.muted }}>Loading contracts…</p>
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <p style={{ color: colors.muted }}>
            You have no contracts to sign right now. We&apos;ll let you know when a new one is available.
          </p>
        </div>
      )}

      {items !== null && membershipNote(items) !== null && (
        <output
          className="block rounded-lg p-4 mb-6 text-sm"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          {membershipNote(items)}
        </output>
      )}

      {items !== null && items.length > 0 && (
        <div className="space-y-8">
          {/* Group templates by scope — general onboarding docs separated
              from product-specific contracts so members can tell at a glance
              which apply to them broadly vs. to a specific investment. */}
          {(['general', 'product'] as const).map((scope) => {
            const group = items.filter((it) => it.scope === scope);
            if (group.length === 0) return null;
            const heading = scope === 'general' ? 'Onboarding documents' : 'Series contracts';
            const subheading =
              scope === 'general'
                ? 'Required for every member as part of the standard onboarding.'
                : 'Specific to the holdings you have started or completed.';
            return (
              <section key={scope}>
                <header className="mb-3">
                  <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.mutedSoft }}>
                    {heading}
                  </h2>
                  <p className="text-xs" style={{ color: colors.muted }}>{subheading}</p>
                </header>
                <ul className="space-y-4">
                  {group.map((item) => {
                    const badge = badgeFor(item.case_state);
                    const isStarting = startingFor === item.template_id;
                    return (
                      <li
                        key={item.template_id}
                        className="rounded-2xl p-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
                        style={{
                          background: colors.surface,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold" style={{ color: colors.ink }}>
                              {item.title}
                            </h3>
                            <span
                              className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                              style={{ background: badge.bg, color: badge.fg }}
                            >
                              {badge.label}
                            </span>
                          </div>
                          <div className="text-xs mb-2" style={{ color: colors.mutedSoft }}>
                            Version {item.current_version}
                            {item.expires_after_days
                              ? ` · Renew every ${item.expires_after_days} days`
                              : ''}
                          </div>
                          {item.description && (
                            <p className="text-sm" style={{ color: colors.inkSoft }}>
                              {item.description}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex sm:flex-col items-stretch sm:items-end gap-2">
                          <button
                            type="button"
                            onClick={() => handlePrimaryAction(item)}
                            disabled={isStarting}
                            className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                            style={{
                              background: colors.navy,
                              color: '#FFFFFF',
                              opacity: isStarting ? 0.6 : 1,
                              cursor: isStarting ? 'wait' : 'pointer',
                            }}
                          >
                            {isStarting ? 'Starting…' : actionLabelFor(item.case_state)}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      <OfferingNotice />
    </>
  );
}
